import type { Game } from 'boardgame.io';
import { INVALID_MOVE } from 'boardgame.io/core';
import type { GrandSnatchState, CardData, Rarity, Suit } from './types';
import { SUITS } from './types';
import { generateSuitCards, shuffle } from './deck';
import { resolveTake } from './scopaAlg';
import { LOCATION_DATABASE, calculateLocationWinner } from './locations';
import { decideWinner } from './scoring';
import { dispatch } from './effect_dispatcher';
import { effectsForCard } from './effects';
import { MISSION_DATABASE } from './missions';
import { splitLayout } from './deckLayout';
import { syndicateFor, applySyndicate } from './syndicates';

// Phase 3 F7 hardening — server-side re-bake of every CardData coming from a
// client deck. The client could ship inflated effects[], a fake mission with
// winScore=999, or a forged rarity. We rebuild the trustable fields from
// (suit, value, type, rarity, mission.templateId) using server canon. If the
// canon doesn't recognise the carrier (unknown suit, out-of-range value,
// unknown mission template), we return null and the caller falls back to
// procedural generation for that player. Vedi `docs/security_audit.md` F7.
const SUIT_SET: ReadonlySet<string> = new Set(SUITS);
const RARITY_SET: ReadonlySet<Rarity> = new Set(['street', 'made', 'kingpin']);
export function sanitizeOwnedCard(raw: unknown): CardData | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<CardData>;
  if (typeof r.id !== 'string' || r.id.length === 0) return null;
  if (typeof r.suit !== 'string' || !SUIT_SET.has(r.suit)) return null;
  const suit = r.suit as Suit;
  if (r.type !== 'Hand' && r.type !== 'Mission') return null;
  const value = Number(r.value);
  if (!Number.isInteger(value)) return null;
  if (r.type === 'Hand' && (value < 1 || value > 6)) return null;
  if (r.type === 'Mission' && value !== 0) return null;
  const rarity: Rarity = r.rarity && RARITY_SET.has(r.rarity) ? r.rarity : 'street';

  if (r.type === 'Hand') {
    const effects = effectsForCard(suit, value, rarity, r.id);
    return {
      id: r.id, suit, value, type: 'Hand', rarity,
      ...(effects ? { effects } : {}),
    };
  }
  // Mission card: re-derive rule + winScore from the (still-trusted) template id.
  const tmplId = r.mission?.templateId;
  const tmpl = typeof tmplId === 'string'
    ? MISSION_DATABASE.find(t => t.id === tmplId && t.suit === suit)
    : undefined;
  if (!tmpl) return null;
  return {
    id: r.id, suit, value: 0, type: 'Mission', rarity,
    mission: {
      id: r.mission?.id ?? `mission_${r.id}`,
      templateId: tmpl.id,
      name: tmpl.name,
      fluff: tmpl.fluff,
      rule: tmpl.rule,
      winScore: tmpl.winScore,
    },
  };
}

// Shape of `setupData` we accept via /games/grand-snatch/create. Everything
// is server-validated up at the lobby middleware (see server.ts); this is
// the read-side contract from inside the game.
export interface MatchSetupData {
  botSeat?: string;
  botDifficulty?: 'easy' | 'medium' | 'hard';
  // F7 v2 — populated by the /create middleware after fetching the cards
  // from the user's active run snapshot. Never accepted from the client
  // raw (the middleware refuses if `runMatch.deckCards` is set client-side).
  // runId/deckKey/targetNodeId legano il match alla run: letti SOLO dal
  // server in record-match, il gioco usa deckCards.
  runMatch?: { deckCards?: CardData[]; runId?: string; deckKey?: 'A' | 'B'; targetNodeId?: string };
  // Match tutorial di una sessione guest (lobby gate + riscatto al register).
  tutorial?: boolean;
  guestSub?: string;
  // Prototipo disposizione a blocchi (vedi deckLayout.ts). Default 'random'.
  layoutMode?: 'random' | 'blocks';
  // Solo simulazione: spegne i bonus Syndicate per confronti di bilanciamento.
  disableSyndicates?: boolean;
}

function getInitialState(setupData?: MatchSetupData): GrandSnatchState {
  const state: GrandSnatchState = {
    players: {
      '0': { hand: [], deck: [], discard: [], stagingArea: [], resources: {}, snatchPoints: 0, playConfirmed: false, stageConfirmed: false, selectedSuits: [], selectedCards: null, readyToPlay: false, avatarId: null },
      '1': { hand: [], deck: [], discard: [], stagingArea: [], resources: {}, snatchPoints: 0, playConfirmed: false, stageConfirmed: false, selectedSuits: [], selectedCards: null, readyToPlay: false, avatarId: null }
    },
    dropzones: [
      { missionCards: [], cards: [], priorityPlayer: '0', lastTaker: null },
      { missionCards: [], cards: [], priorityPlayer: '1', lastTaker: null }
    ],
    roundCount: 0,
    stagedMoves: { '0': null, '1': null },
    activeLocation: null,
    matchSummary: { snatchEvents: [] },
  };
  // Disposizione a blocchi = regola standard (2026-09). 'random' resta solo
  // per confronti in simulazione con la distribuzione storica.
  state.layoutMode = setupData?.layoutMode === 'random' ? 'random' : 'blocks';
  if (setupData?.disableSyndicates) state.syndicatesDisabled = true;

  // F7 v2 — run-match pre-bake. The creator (seat 0) already had their deck
  // resolved server-side from `user_runs.decks_snapshot[deckKey]`. We park
  // it in selectedCards + derive selectedSuits so the draft picker on the
  // client can skip rendering for seat 0. The selectDeck move ignores
  // payload.cards while these are non-empty (see the move handler).
  const runDeckCards = setupData?.runMatch?.deckCards;
  if (Array.isArray(runDeckCards) && runDeckCards.length > 0) {
    state.players['0'].selectedCards = runDeckCards;
    const uniqueSuits: Suit[] = [];
    for (const c of runDeckCards) {
      if (c?.suit && !uniqueSuits.includes(c.suit)) uniqueSuits.push(c.suit);
      if (uniqueSuits.length === 2) break;
    }
    state.players['0'].selectedSuits = uniqueSuits;
  }

  return state;
}

export const GrandSnatchGame: Game<GrandSnatchState> = {
  name: 'grand-snatch',
  setup: (_ctx, setupData) => getInitialState(setupData as MatchSetupData | undefined),

  playerView: ({ G, ctx, playerID }) => {
    // Spectator (playerID null/undefined): GS è 1v1 senza pubblico ufficiale;
    // un osservatore esterno che facesse onSync con playerID=null otterrebbe
    // dropzone/score/phase visibili — comodo per spiare una partita in corso.
    // Restituisce uno stato sentinel che il client non sa renderizzare. bgio
    // già rifiuta le sync con playerID concreto + credenziali sbagliate
    // (master.ts onSync), questo chiude la spectator-side. Vedi
    // docs/security_audit.md F5.
    if (playerID === undefined || playerID === null) {
      return { spectatorDenied: true } as unknown as GrandSnatchState;
    }
    const newG = JSON.parse(JSON.stringify(G)) as GrandSnatchState;

    Object.keys(newG.players).forEach(pId => {
      if (pId !== playerID) {
        newG.players[pId].hand = newG.players[pId].hand.map(() => ({ id: 'hidden', value: 0, suit: 'hidden' as any, type: 'hidden' as any }));
        newG.players[pId].deck = newG.players[pId].deck.map(() => ({ id: 'hidden', value: 0, suit: 'hidden' as any, type: 'hidden' as any }));
      }
    });

    if (ctx.phase === 'play') {
        Object.keys(newG.stagedMoves).forEach(pId => {
          if (pId !== playerID && newG.stagedMoves[pId]) {
            const move = newG.stagedMoves[pId]!;
            newG.stagedMoves[pId] = { 
              card: { id: 'hidden', value: 0, suit: 'hidden' as any, type: 'hidden' as any }, 
              dropzoneIndex: move.dropzoneIndex,
              targetIndex: move.targetIndex
            };
          }
        });
    }

    return newG;
  },

  phases: {
    draft: {
      start: true,
      turn: {
        activePlayers: { all: 'draft' }
      },
      moves: {
        // Long-form move con `ignoreStaleStateID: true`. Necessario perché
        // dopo `POST /auth/matches/:matchID/commit-deck` il server muta lo
        // state via `db.setState` SENZA passare per il Master di bgio →
        // niente broadcast → la stateID locale del client resta vecchia.
        // Quando il client poi dispatcha questo `selectDeck({avatarId})`
        // per confermare readyToPlay, l'action porta una stateID staled
        // e il Master la rifiuterebbe con `invalid stateID`. Il flag
        // `ignoreStaleStateID` lo permette di passare; la move è
        // idempotente (early-return su `readyToPlay`) e legge sempre
        // dal G server-fresh, quindi è safe saltare il check.
        selectDeck: {
          ignoreStaleStateID: true,
          move: (
            { G, playerID },
            payload: { suits?: any[]; cards?: CardData[]; avatarId?: number | null }
          ) => {
            const pid = playerID!;
            if (G.players[pid].readyToPlay) return;

            // F7 v2 — run/commit-deck path: cards already pre-baked into G
            // (server fetched them and either pre-populated at setup or
            // mutated via db.setState in commit-deck). Ignore payload.cards
            // entirely. Only avatarId is accepted from the client because
            // it's purely cosmetic.
            const preBaked = G.players[pid].selectedCards;
            if (preBaked && preBaked.length > 0) {
              if (payload && payload.avatarId !== undefined) {
                const a = payload.avatarId;
                G.players[pid].avatarId = a === null || (Number.isInteger(a) && a! >= 0 && a! <= 11) ? a : null;
              }
              G.players[pid].readyToPlay = true;
              return;
            }

            // Legacy/fallback path: bot calls without pre-bake (picks random
            // suits), or guest mode without a real deck. Sanitization in
            // draft.onEnd protects against malicious payloads.
            if (!payload || !Array.isArray(payload.suits) || payload.suits.length !== 2) return;
            G.players[pid].selectedSuits = payload.suits;
            G.players[pid].selectedCards = payload.cards ?? null;
            if (payload.avatarId !== undefined) {
              const a = payload.avatarId;
              G.players[pid].avatarId = a === null || (Number.isInteger(a) && a! >= 0 && a! <= 11) ? a : null;
            }
            G.players[pid].readyToPlay = true;
          },
        },
      },
      endIf: ({ G }) => {
        if (G.players['0'].readyToPlay && G.players['1'].readyToPlay) {
          return true;
        }
      },
      onEnd: ({ G, random }) => {
        const p0Suits = G.players['0'].selectedSuits;
        const p1Suits = G.players['1'].selectedSuits;
        const p0Cards = G.players['0'].selectedCards;
        const p1Cards = G.players['1'].selectedCards;

        const prng = () => random.Number();
        // Shared exclusion set so the 4 mission cards generated procedurally
        // come from 4 distinct templates. Pre-built decks supply their own
        // missions so they don't consume from this set.
        const usedMissionTemplates = new Set<string>();

        // Cards coming from a user's owned deck have UUID ids. We re-prefix them
        // with the player slot so ownerOf() in effect_dispatcher.ts (which
        // matches "p0_*" / "p1_*") still works. Procedural decks already use
        // the prefixed convention via generateSuitCards.
        //
        // F7 hardening: every card is re-sanitized server-side first — the
        // client could otherwise inject effects[] / mission.winScore arbitrari
        // and have them executed in-match. If ANY card fails sanitization, the
        // whole selection is dropped and we fall back to procedural generation
        // for that player. Stricter than ignoring just the bad card so a
        // partial-tamper can't slip through unnoticed.
        const adoptDeckSafe = (
          cards: CardData[],
          playerPrefix: 'p0' | 'p1'
        ): CardData[] | null => {
          const out: CardData[] = [];
          for (const c of cards) {
            const clean = sanitizeOwnedCard(c);
            if (!clean) return null;
            out.push({ ...clean, id: `${playerPrefix}_${clean.id}` });
          }
          return out;
        };

        const p0Adopted = p0Cards && p0Cards.length > 0 ? adoptDeckSafe(p0Cards, 'p0') : null;
        const p1Adopted = p1Cards && p1Cards.length > 0 ? adoptDeckSafe(p1Cards, 'p1') : null;

        const p0Deck: CardData[] = p0Adopted ?? [
          ...generateSuitCards(p0Suits[0], 'p0_a', prng, usedMissionTemplates),
          ...generateSuitCards(p0Suits[1], 'p0_b', prng, usedMissionTemplates),
        ];
        const p1Deck: CardData[] = p1Adopted ?? [
          ...generateSuitCards(p1Suits[0], 'p1_a', prng, usedMissionTemplates),
          ...generateSuitCards(p1Suits[1], 'p1_b', prng, usedMissionTemplates),
        ];

        // Syndicate: coppie di suit con bonus passivo (syndicates.ts). Si
        // applica sulle carte già sanitizzate/generate, mai su dati client.
        for (const [pid, deck] of [['0', p0Deck], ['1', p1Deck]] as const) {
          const suits = Array.from(new Set(deck.map(c => c.suit)));
          const syn = G.syndicatesDisabled ? null : syndicateFor(suits);
          G.players[pid].syndicate = syn?.id ?? null;
          if (syn) applySyndicate(deck, syn.id);
        }

        // Modalità a blocchi: un mazzo del giocatore con disposizione valida
        // NON viene mischiato — l'ordine delle Hand card è [tavolo, mano1,
        // mano2] (deckLayout.ts). I mazzi procedurali restano casuali.
        const blocks = G.layoutMode === 'blocks';
        const p0Ordered = blocks && p0Adopted !== null && splitLayout(p0Deck) !== null;
        const p1Ordered = blocks && p1Adopted !== null && splitLayout(p1Deck) !== null;
        if (!p0Ordered) shuffle(p0Deck, () => random.Number());
        if (!p1Ordered) shuffle(p1Deck, () => random.Number());

        // Picks one Mission card at random from a deck and removes it. The
        // randomness is explicit (non si appoggia all'ordine post-shuffle) così
        // se in futuro lo shuffle cambia o salta, la scelta della mission
        // resta uniforme tra tutte quelle disponibili nel deck.
        const pickRandomMission = (deck: CardData[]): CardData | undefined => {
          const indices: number[] = [];
          for (let i = 0; i < deck.length; i++) {
            if (deck[i].type === 'Mission') indices.push(i);
          }
          if (indices.length === 0) return undefined;
          const pick = indices[Math.floor(random.Number() * indices.length)];
          return deck.splice(pick, 1)[0];
        };

        const p0Mission = pickRandomMission(p0Deck)!;
        const p1Mission = pickRandomMission(p1Deck)!;

        // Send the second mission card to the player's discard pile (instead
        // of dropping it on the floor) so it's available as future salvage
        // material for card effects. evaluateScoringRule filters Hand-only,
        // so this mission in `discard` does NOT contribute to scoring counts.
        const p0Trash = pickRandomMission(p0Deck);
        if (p0Trash) G.players['0'].discard.push(p0Trash);
        const p1Trash = pickRandomMission(p1Deck);
        if (p1Trash) G.players['1'].discard.push(p1Trash);

        const p0Table = p0Deck.splice(0, 4);
        const p1Table = p1Deck.splice(0, 4);

        G.players['0'].hand = p0Deck.splice(0, 4);
        G.players['1'].hand = p1Deck.splice(0, 4);

        G.players['0'].deck = p0Deck;
        G.players['1'].deck = p1Deck;

        G.dropzones[0].missionCards = [p0Mission];
        G.dropzones[1].missionCards = [p1Mission];

        if (blocks) {
          // Ogni giocatore mette 2 carte del proprio blocco tavolo su ciascuna
          // dropzone; QUALI 2 è casuale (resta un minimo di incertezza, e non
          // si può costruire a tavolino una dropzone da svuotare).
          const rng = () => random.Number();
          shuffle(p0Table, rng);
          shuffle(p1Table, rng);
          G.dropzones[0].cards = shuffle([...p0Table.slice(0, 2), ...p1Table.slice(0, 2)], rng);
          G.dropzones[1].cards = shuffle([...p0Table.slice(2, 4), ...p1Table.slice(2, 4)], rng);
        } else {
          const tableCards = shuffle([...p0Table, ...p1Table], () => random.Number());
          G.dropzones[0].cards = tableCards.slice(0, 4);
          G.dropzones[1].cards = tableCards.slice(4, 8);
        }

        // Randomize Location. Every Location rule is suit-agnostic by design
        // (see locations.ts), so any of them is valid regardless of what the
        // two players drafted — no eligibility filtering needed.
        const locIdx = Math.floor(random.Number() * LOCATION_DATABASE.length);
        G.activeLocation = LOCATION_DATABASE[locIdx];

        // Randomize initial dropzone priority (mirrors Godot setup_state.gd).
        // The two dropzones always have opposite priority players.
        if (random.Number() < 0.5) {
          G.dropzones[0].priorityPlayer = '0';
          G.dropzones[1].priorityPlayer = '1';
        } else {
          G.dropzones[0].priorityPlayer = '1';
          G.dropzones[1].priorityPlayer = '0';
        }
      },
      next: 'play'
    },
    play: {
      onBegin: ({ G }) => {
        G.stagedMoves = { '0': null, '1': null };
        G.players['0'].playConfirmed = false;
        G.players['1'].playConfirmed = false;
      },
      turn: {
        activePlayers: { all: 'play' }
      },
      moves: {
        playCard: ({ G, playerID }, cardId: string, dropzoneIndex: number, targetIndex?: number) => {
          const playerId = playerID!;
          if (G.players[playerId].playConfirmed || G.stagedMoves[playerId]) return;
          // Input non fidato: una dropzone fuori range farebbe sparire la carta
          // (tolta dalla mano ma mai risolta in play.onEnd).
          if (dropzoneIndex !== 0 && dropzoneIndex !== 1) return INVALID_MOVE;
          const hand = G.players[playerId].hand;
          const cardIdx = hand.findIndex(c => c.id === cardId);
          if (cardIdx !== -1) {
            const card = hand[cardIdx];
            const maxTarget = G.dropzones[dropzoneIndex].cards.length;
            const rawTarget = Number.isInteger(targetIndex) ? targetIndex! : 0;
            const safeTarget = Math.min(Math.max(rawTarget, 0), maxTarget);
            G.stagedMoves[playerId] = { card, dropzoneIndex, targetIndex: safeTarget };
            hand.splice(cardIdx, 1);
            // NB: on_play NON viene dispatchato qui ma in play.onEnd. Prima,
            // play→undo→play ripetuti accumulavano give_resources / steal /
            // held_charge a ogni giocata, perché undoCard non annulla effetti.
          }
        },
        undoCard: ({ G, playerID }) => {
          const playerId = playerID!;
          if (G.players[playerId].playConfirmed) return;
          const move = G.stagedMoves[playerId];
          if (move) {
            G.players[playerId].hand.push(move.card);
            G.stagedMoves[playerId] = null;
          }
        },
        confirmPlay: ({ G, playerID }) => {
          const playerId = playerID!;
          if (G.stagedMoves[playerId]) {
             G.players[playerId].playConfirmed = true;
          }
        }
      },
      endIf: ({ G }) => {
        if (G.players['0'].playConfirmed && G.players['1'].playConfirmed) {
          return true;
        }
      },
      onEnd: ({ G }) => {
        if (!G.stagedMoves['0'] || !G.stagedMoves['1']) return;

        const moves = [
          { playerId: '0', move: G.stagedMoves['0'] },
          { playerId: '1', move: G.stagedMoves['1'] }
        ];

        // Snatch is not allowed on the very last turn/hand of the match
        // on_play risolto qui: le mosse sono confermate e non più annullabili.
        // Ordine deterministico: prima il giocatore con priorità su dz0.
        const playOrder: Array<'0' | '1'> = G.dropzones[0].priorityPlayer === '1' ? ['1', '0'] : ['0', '1'];
        for (const pid of playOrder) {
          const staged = G.stagedMoves[pid];
          if (staged) dispatch(G, { trigger: 'on_play', card: staged.card });
        }

        const isLastPlay = G.players['0'].hand.length === 0 &&
                           G.players['1'].hand.length === 0 &&
                           G.players['0'].deck.length === 0 &&
                           G.players['1'].deck.length === 0;

        [0, 1].forEach(dzIdx => {
          const dropzone = G.dropzones[dzIdx];
          const dzMoves = moves.filter(m => m.move && m.move.dropzoneIndex === dzIdx);

          if (dzMoves.length === 2) {
             dzMoves.sort((a, _b) => a.playerId === dropzone.priorityPlayer ? -1 : 1);
          }

          let turnSnatchPlayer = null;
          let pendingSnatchEvent = null;

          dzMoves.forEach(({ playerId, move }) => {
            const card = move!.card;
            const takenCards = resolveTake(card.value, dropzone.cards);

            if (takenCards.length > 0) {
              dropzone.lastTaker = playerId;
              G.players[playerId].stagingArea.push({ ...card });

              takenCards.forEach(tc => {
                const dropIdx = dropzone.cards.findIndex((dc: CardData) => dc.id === tc.id);
                if (dropIdx !== -1) dropzone.cards.splice(dropIdx, 1);
                G.players[playerId].stagingArea.push({ ...tc });
              });

              dispatch(G, { trigger: 'on_capture', taker: card, takenCards });
              takenCards.forEach(tc => {
                dispatch(G, {
                  trigger: 'on_captured',
                  victim: tc,
                  takerCard: card,
                  takerPlayerID: playerId,
                });
              });

              if (dropzone.cards.length === 0) {
                turnSnatchPlayer = playerId;
                pendingSnatchEvent = {
                  snatcherID: playerId as '0' | '1',
                  capturedCards: [card, ...takenCards].map(c => ({ ...c })),
                };
              } else {
                turnSnatchPlayer = null;
                pendingSnatchEvent = null;
              }
            } else {
              const tIdx = move!.targetIndex ?? 0;
              dropzone.cards.splice(tIdx, 0, { ...card });
              dispatch(G, { trigger: 'on_table', card });
              turnSnatchPlayer = null;
              pendingSnatchEvent = null;
            }
          });

          if (!isLastPlay && turnSnatchPlayer && dropzone.cards.length === 0 && pendingSnatchEvent) {
            G.players[turnSnatchPlayer].snatchPoints += 1;
            G.matchSummary.snatchEvents.push(pendingSnatchEvent);
            // Syndicate Cartel: ogni snatch paga +1 drug e +1 money.
            const snatcher = G.players[turnSnatchPlayer];
            if (snatcher.syndicate === 'cartel') {
              snatcher.resources.drug = (snatcher.resources.drug || 0) + 1;
              snatcher.resources.money = (snatcher.resources.money || 0) + 1;
            }
            // on_snatch sulla carta giocata che ha svuotato la dropzone (la
            // copia ora nello staging del giocatore).
            const snatchCard = snatcher.stagingArea.find(c => c.id === pendingSnatchEvent!.capturedCards[0]?.id);
            if (snatchCard) dispatch(G, { trigger: 'on_snatch', card: snatchCard });
          }
        });
      },
      next: 'stage'
    },
    stage: {
      onBegin: ({ G }) => {
        // Auto-confirm: se un player ha stagingArea vuota non c'è niente da
        // "estrarre" — niente click richiesto. Se entrambi vuoti, endIf scatta
        // immediatamente e la fase avanza senza alcun input. Se solo uno è
        // vuoto, l'altro deve confermare normalmente (umano via click, bot via
        // race-avoidance che ora vede già opp.stageConfirmed=true).
        G.players['0'].stageConfirmed = G.players['0'].stagingArea.length === 0;
        G.players['1'].stageConfirmed = G.players['1'].stagingArea.length === 0;
      },
      turn: {
        activePlayers: { all: 'stage' }
      },
      moves: {
        confirmStage: ({ G, playerID }) => {
          G.players[playerID!].stageConfirmed = true;
        }
      },
      endIf: ({ G }) => {
        if (G.players['0'].stageConfirmed && G.players['1'].stageConfirmed) {
          return true;
        }
      },
      onEnd: ({ G }) => {
        ['0', '1'].forEach(playerId => {
           const p = G.players[playerId];
           p.stagingArea.forEach(c => {
              p.resources[c.suit] = (p.resources[c.suit] || 0) + c.value;
              p.discard.push(c);
           });
           p.stagingArea = [];
        });

        // --- Stateful effects: round-boundary tick ---
        // Dispatched after the staging drain (so resources reflect this round)
        // but BEFORE the hand refill, so freshly drawn cards don't get a tick
        // the same round they're drawn. Scans hands (held_charge) and dropzone
        // cards (ripening_loot, time_bomb, decaying_value).
        dispatch(G, { trigger: 'on_round_end', roundCount: G.roundCount });

        // Tick down freeze locks. frozenTurns is a plain CardData field (not an
        // effect instance), so it's scanned here directly, not via the dispatcher.
        for (const dz of G.dropzones) {
          for (const c of dz.cards) {
            if (c.frozenTurns && c.frozenTurns > 0) {
              c.frozenTurns -= 1;
              if (c.frozenTurns === 0) delete c.frozenBy;
            }
          }
        }

        if (G.players['0'].hand.length === 0 && G.players['1'].hand.length === 0) {
          if (G.players['0'].deck.length > 0) {
            G.players['0'].hand.push(...G.players['0'].deck.splice(0, 4));
            G.players['1'].hand.push(...G.players['1'].deck.splice(0, 4));
            // Arriva la mano 2: on_draw per ogni carta pescata (es. Sleeper).
            for (const pid of ['0', '1'] as const) {
              for (const c of G.players[pid].hand) dispatch(G, { trigger: 'on_draw', card: c });
            }
          }
        }

        G.stagedMoves = { '0': null, '1': null };
        G.players['0'].playConfirmed = false;
        G.players['1'].playConfirmed = false;
        G.players['0'].stageConfirmed = false;
        G.players['1'].stageConfirmed = false;

        G.roundCount += 1;
        G.dropzones[0].priorityPlayer = G.dropzones[0].priorityPlayer === '0' ? '1' : '0';
        G.dropzones[1].priorityPlayer = G.dropzones[1].priorityPlayer === '0' ? '1' : '0';

        // Check Internal Game Over and sweep leftovers if game is ending
        const isGameEnd = G.players['0'].hand.length === 0 &&
                          G.players['1'].hand.length === 0 &&
                          G.players['0'].deck.length === 0 &&
                          G.players['1'].deck.length === 0;

        if (isGameEnd) {
           [0, 1].forEach(dzIdx => {
             const dz = G.dropzones[dzIdx];
             if (dz.cards.length > 0 && (dz.lastTaker === '0' || dz.lastTaker === '1')) {
                const takerPlayerID = dz.lastTaker;
                const takerPlayer = G.players[takerPlayerID];
                dz.cards.forEach(c => {
                  const copy = { ...c };
                  takerPlayer.stagingArea.push(copy);
                  // Sweep di fine partita: la carta viene presa dal lastTaker
                  // anche senza una carta giocata. on_captured deve comunque
                  // scattare.
                  dispatch(G, {
                    trigger: 'on_captured',
                    victim: copy,
                    takerCard: copy,
                    takerPlayerID,
                  });
                });
                dz.cards = [];
             }
           });
        }
      },
      next: ({ G }) => {
        const isGameEnd = G.players['0'].hand.length === 0 &&
                          G.players['1'].hand.length === 0 &&
                          G.players['0'].deck.length === 0 &&
                          G.players['1'].deck.length === 0;
        return isGameEnd ? 'sweep' : 'play';
      }
    },
    sweep: {
      onBegin: ({ G }) => {
        // Do NOT auto-confirm even if staging area is empty, so that the player
        // can see what leftovers the opponent swept before the game ends.
        G.players['0'].stageConfirmed = false;
        G.players['1'].stageConfirmed = false;
      },
      turn: {
        activePlayers: { all: 'stage' }
      },
      moves: {
        confirmStage: ({ G, playerID }) => {
          G.players[playerID!].stageConfirmed = true;
        }
      },
      endIf: ({ G }) => {
        if (G.players['0'].stageConfirmed && G.players['1'].stageConfirmed) {
          return true;
        }
      },
      onEnd: ({ G }) => {
        ['0', '1'].forEach(playerId => {
           const p = G.players[playerId];
           p.stagingArea.forEach(c => {
              p.resources[c.suit] = (p.resources[c.suit] || 0) + c.value;
              p.discard.push(c);
           });
           p.stagingArea = [];
        });
      },
      next: 'play'
    }
  },

  endIf: ({ G, ctx }) => {
    // The game ends when both players have no cards in hand, no cards in deck, 
    // AND we have circled back to the clean 'play' phase (meaning the final 'stage' phase completely finished).
    if (
      ctx.phase === 'play' &&
      G.roundCount > 0 &&
      G.players['0'].hand.length === 0 &&
      G.players['1'].hand.length === 0 &&
      G.players['0'].deck.length === 0 &&
      G.players['1'].deck.length === 0 &&
      G.stagedMoves['0'] === null &&
      G.stagedMoves['1'] === null
    ) {
      const locWinner = calculateLocationWinner(G);
      const locScore = G.activeLocation?.winScore ?? 1;

      let locBonus0 = 0;
      let locBonus1 = 0;
      if (locWinner === '0') locBonus0 = locScore;
      else if (locWinner === '1') locBonus1 = locScore;

      // Resolve missions: each dropzone holds at most one active Mission card,
      // each carries its own ScoringRule and winScore (1 or 2). Ties give 0.
      const missionResults: Array<{
        missionId: string;
        name: string;
        winner: string | null;
        winScore: number;
      }> = [];
      let missionBonus0 = 0;
      let missionBonus1 = 0;
      for (const dz of G.dropzones) {
        for (const mc of dz.missionCards) {
          if (!mc.mission) continue;
          const winner = decideWinner(
            mc.mission.rule,
            { discard: G.players['0'].discard, resources: G.players['0'].resources },
            { discard: G.players['1'].discard, resources: G.players['1'].resources },
          );
          missionResults.push({
            missionId: mc.mission.id,
            name: mc.mission.name,
            winner,
            winScore: mc.mission.winScore,
          });
          if (winner === '0') missionBonus0 += mc.mission.winScore;
          else if (winner === '1') missionBonus1 += mc.mission.winScore;
        }
      }

      const score0 = G.players['0'].snatchPoints + locBonus0 + missionBonus0;
      const score1 = G.players['1'].snatchPoints + locBonus1 + missionBonus1;

      const breakdown = {
        score0,
        score1,
        locWinner,
        locScore,
        locBonus0,
        locBonus1,
        missionResults,
        missionBonus0,
        missionBonus1,
      };

      if (score0 > score1) {
        return { winner: '0', ...breakdown };
      } else if (score1 > score0) {
        return { winner: '1', ...breakdown };
      } else {
        return { draw: true, ...breakdown };
      }
    }
  }
};
